// App.jsx
'use strict';
import React from 'react';
import PropTypes from 'prop-types';
import { Route, Routes, Outlet, useLocation } from 'react-router-dom';
import styled, {
  createGlobalStyle,
  StyleSheetManager,
} from 'styled-components';
import styledNormalize from 'styled-normalize';
import { withTranslation } from 'react-i18next';

import media from '../style/media.js';
import {
  footerNavigationHeight,
  iPhoneXSeriesFooterPaddingInPWA,
} from '../style/variables.js';

import RequireAuth from '../container/RequireAuth.js';
import RequireNoneAuth from '../container/RequireNoneAuth.js';
import ModalView from '../container/ModalView.js';
import ModalsLoader from '../container/ModalsLoader.js';
import Redirect from '../container/Redirect.js';
import ErrorPage from '../page/ErrorPage.jsx';
import MongoObjectIdMatcher from '../component/MongoObjectIdMatcher.jsx';
import LeakerBanWarning from '../component/LeakerBanWarning.jsx';
import SkipHydration from '../component/SkipHydration.jsx';
import UniversalContainer from '../component/UniversalContainer.jsx';
import UniversalComponent from '../component/UniversalComponent.jsx';
import ChunkLoading from '../component/ChunkLoading.jsx';
import GeneralErrorToastr from '../component/GeneralErrorToastr.jsx';
import DiscoverShowMoreWrapper from '../component/DiscoverShowMoreWrapper.jsx';
import RegExpRoute from '../component/RegExpRoute.jsx';
import MultiPathRoute from '../component/MultiPathRoute.jsx';
import ModalTracking from '../component/ModalTracking.jsx';
import ReduxHelmet from '../container/ReduxHelmet.js';
import { LIVESTREAM_PATH } from '../resource/liveStreamConstants.js';
import { addvalue as addvalueModals } from '../resource/modalId.js';
import { shouldShowPwaSafariToast } from '../resource/UIPreferences.js';
import { getIsIphoneXSeriesPWA } from '../resource/getUserAgent.js';
import { searchType, SEARCH_MODAL_ID } from '../resource/searchConstants.js';
import { CHAT_ID as AUTO_CHAT_CHAT_ID } from '../resource/autoChatConstants.js';
import env from '../resource/env.js';
import { Progress as JoinProgress } from '../resource/joinConstants.js';
import {
  ManageProfileType,
  ProfileListType,
  ProfileListPublishStatus,
} from '../resource/profileConstants.js';
import logoSource from '../../img/share.jpg';
import { FAKE_CHAT_ID } from '../resource/chatroomConstants.js';
import {
  login as loginModal,
  register as registerModal,
} from '../resource/modalId.js';
import {
  BOTTOM_NAVIGATION_PATHS,
  FOOTER_PATHS,
} from '../resource/getShouldShowFooter.js';
import { TRACK_UTM_LIST } from '../resource/fetchOptionHeader.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { ModalId } from '../resource/mixpanel.js';
import SeoLinkTagsHelmet from '../container/SeoLinkTagsHelmet.js';

const universalConfig = {
  loading: <ChunkLoading />,
  error: <GeneralErrorToastr />,
};

const GlobalStyle = createGlobalStyle`
  ${styledNormalize};
  *, ::after, ::before { box-sizing: border-box; }
  html {
    &::-webkit-scrollbar {
      display: none;
    }
    :root {
      --safe-area-bottom: env(safe-area-inset-bottom, ${iPhoneXSeriesFooterPaddingInPWA}px);
      --space-to-pinned-notification: 0px;
      --space-to-pinned-notification-even-on-desktop: 0px;
    }
  }
  body {
    background-color: #191919;
    color: #fff;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue",
      Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
    font-weight: 400;
    -webkit-tap-highlight-color: transparent;
    overscroll-behavior-y: contain;
    &::-webkit-scrollbar {
      display: none;
    }
  }
  button {
    background-color: rgba(0, 0, 0, 0);
    cursor: pointer;
    &:disabled {
      cursor: not-allowed;
    }
  }
  input::placeholder,
  input::-webkit-input-placeholder,
  input::-moz-placeholder {
    line-height: normal !important;
    vertical-align: middle;
  }
  a { color: inherit; text-decoration: none; }
  a > * { opacity: inherit; }
  a:hover {
    opacity: .7;

    ${media.mobile`
      opacity: 1;
    `};
  }
  iframe {
    border-width: 0px;
  }

  ${media.tablet`
    html { touch-action: manipulation; }
  `};
`;

const addvalueRegExp = /^\/(?!login|404)/i;

const appVersion = env.TAG_NAME || env.BRANCH_NAME || 'local';
const appSha = env.SHORT_SHA;

// only preconnect to critical domains, others just dnsPrefetch
// https://web.dev/preconnect-and-dns-prefetch/
const preconnectLinks = [
  { url: 'https://api.swag.live', preconnect: 1, dnsPrefetch: 1 },
  { url: 'https://public.swag.live', preconnect: 1, dnsPrefetch: 1 },
  { url: 'https://js.pusher.com', preconnect: 1, dnsPrefetch: 1 },
  { url: 'https://api-js.mixpanel.com', preconnect: 0, dnsPrefetch: 1 },
  { url: 'https://sentry.io', preconnect: 0, dnsPrefetch: 1 },
];
const ClientContainer = props => {
  // eslint-disable-next-line react/prop-types
  const { filename } = props;

  return (
    <SkipHydration
      filename={filename}
      render={() => <UniversalContainer {...props} />}
    />
  );
};

const App = ({
  defaultOgImageUrl,
  defaultOgTitle,
  defaultOgDescription,
  brandingName = 'SWAG',
  request,
  i18n,
  t,
}) => {
  const location = useLocation();
  const [hostname] = request?.get('x-forwarded-host')?.split(',') || [
    request?.get('host') || 'swag.live',
  ];
  const twitterImageSource = `https://${hostname}${logoSource}`;
  const language = request?.language || i18n?.language || 'en';
  const displayOgImageUrl = defaultOgImageUrl || twitterImageSource;

  const displayOgTitle = t(defaultOgTitle || 'seo-home-title', {
    ns: TranslationNamespace.SEO,
  });
  const displayOgDescription = t(
    defaultOgDescription || 'seo-home-description',
    {
      ns: TranslationNamespace.SEO,
    }
  );
  const isDefaultOgChanged = !!(
    defaultOgImageUrl ||
    defaultOgTitle ||
    defaultOgDescription
  );
  const searchParams = new URLSearchParams(
    isDefaultOgChanged ? location.search : ''
  );
  [...searchParams.keys()]
    .filter(key => !TRACK_UTM_LIST.includes(key.toLowerCase()))
    .forEach(key => {
      searchParams.delete(key);
    });
  const search = searchParams.toString();
  return (
    <StyledApp>
      <ReduxHelmet ogTitle={displayOgTitle} brandingName={brandingName}>
        <html lang={language} />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />

        <meta name="author" content="SWAG" />
        <meta name="description" content={displayOgDescription} />
        <meta name="RATING" content="RTA-5042-1996-1400-1577-RTA" />

        <meta
          property="og:url"
          content={`https://${hostname}${search ? `?${search}` : ''}`}
        />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={displayOgTitle} />
        <meta property="og:description" content={displayOgDescription} />
        <meta property="og:site_name" content="SWAG" />
        <meta property="twitter:card" content="summary" />
        <meta property="twitter:site" content="@swaglive_app" />
        <meta property="twitter:image" content={displayOgImageUrl} />
        <meta property="og:image" content={displayOgImageUrl} />
        <meta property="og:image:width" content="1600" />
        <meta property="og:image:height" content="900" />
        <meta property="fb:app_id" content={env.FACEBOOK_APP_ID} />
        <meta property="webapp:version" content={appVersion} />
        {appSha && <meta property="webapp:short_sha" content={appSha} />}

        {preconnectLinks
          .filter(link => link.preconnect)
          .map(link => {
            return <link key={link.url} rel="preconnect" href={link.url} />;
          })}
        {preconnectLinks
          .filter(link => link.dnsPrefetch)
          .map(link => {
            return <link key={link.url} rel="dns-prefetch" href={link.url} />;
          })}
        {env.MIXPANEL_CUSTOM_LIB_URL && (
          <link
            rel="preload"
            href={env.MIXPANEL_CUSTOM_LIB_URL}
            as="script"
            type="text/javascript"
            fetchPriority="low"
          />
        )}
      </ReduxHelmet>
      <SeoLinkTagsHelmet
        url={`https://${hostname}${location.pathname}${search ? `?${search}` : ''}`}
      />
      <GlobalStyle />
      {/* RemoteConfigMonitor should be stay before and components which would modify url.
          ex: url loaders and history modifier */}
      <UniversalContainer filename="RemoteConfigMonitor" />
      <ClientContainer filename="GtmLoader" />
      <ClientContainer filename="FpJsLoader" />
      <ClientContainer filename="HistoryModifier" />
      <ClientContainer filename="UrlOAuthLoginLoader" />
      <ClientContainer filename="UrlOtpLoginLoader" />
      <ClientContainer filename="UrlPopupLoader" />
      <ClientContainer filename="UrlOrderLoader" />
      <ClientContainer filename="MonitorLocationChange" />
      <ClientContainer
        filename="CustomerServiceScriptLoader"
        timeoutMsec={10000}
      />
      <ClientContainer filename="PusherEmitter" />
      <ClientContainer filename="MixpanelChores" />
      <ClientContainer filename="PersistMiddlewareLoader" />
      <ClientContainer filename="PingCheckHandler" />
      <ClientContainer filename="ServiceWorkerChores" />
      <ClientContainer filename="PageVisibilityHandler" />
      <ClientContainer filename="DeviceSizeHandler" />
      <ClientContainer filename="UserSessionTick" />
      <ClientContainer filename="ResumableGuardian" />
      <ClientContainer filename="TurnstileLoader" />
      <ClientContainer filename="GetRemoteConfigDataWithTimeout" />
      <ClientContainer filename="IframeProtector" />
      <ClientContainer filename="PushNotificationHandler" />
      <ClientContainer filename="PollingPusherChannelData" />
      <ClientContainer filename="DebugTracking" />
      <ClientContainer filename="StorageCleaner" />
      <ClientContainer filename="WebVitalsLoader" />
      <ClientContainer filename="HotjarLoader" />
      <ClientContainer filename="PhoneVerifyRewardHandler" />
      <LeakerBanWarning />
      {/* Can't just use .*livestream. Otherwise, some feeds or leaderboard might be filtered out. */}
      <RegExpRoute
        regExp={
          /^(?!\/user\/[^/]+\/livestream|\/age-verify|\/join|\/revenue).*/i
        }
        element={
          <ClientContainer filename="AgeVerificationPinnedNotification" />
        }
      />
      <RegExpRoute
        regExp={/^(?!.*livestream|\/join|\/revenue).*/i}
        element={<ClientContainer filename="PopupNotificationList" />}
      />
      <RegExpRoute
        regExp={
          /^\/(?!settings|404|tos|privacy|error|login|addvalue|join|revenue|standalone|iframe)/i
        }
        element={<UniversalContainer filename="Landing" />}
      />
      <RegExpRoute
        regExp={
          /^\/(?!settings|404|tos|privacy|error|login|join|revenue|standalone|iframe)/i
        }
        element={<UniversalContainer filename="AgeVerifierModal" />}
      />
      <RegExpRoute
        regExp={/^\/(?!404)/i}
        element={
          <>
            <ClientContainer filename="FlightCheck" />
            <ClientContainer filename="UrlQueryLoader" />
          </>
        }
      />
      <RegExpRoute
        regExp={addvalueRegExp}
        element={
          <ModalsLoader ids={Object.values(addvalueModals)}>
            <UniversalContainer filename="AddvalueModals" />
          </ModalsLoader>
        }
      />
      <MultiPathRoute
        paths={BOTTOM_NAVIGATION_PATHS}
        element={
          <UniversalContainer filename="NavFooter" {...universalConfig} />
        }
      />
      <MultiPathRoute
        paths={['/', '/chat', '/chat/:chatroomId']}
        element={<ClientContainer filename="NotificationReadList" />}
      />

      {/* FloatingActionButtons */}
      <ClientContainer filename="FloatingActionButtons">
        <MultiPathRoute
          paths={['/', '/following', '/video', '/feed', '/chat']}
          element={<ClientContainer filename="UserEventEntranceButton" />}
        />
        <MultiPathRoute
          paths={['/', '/following', '/video', '/story', '/feed', '/chat']}
          element={<ClientContainer filename="BackpackEntranceButton" />}
        />
        <MultiPathRoute
          paths={[
            '/',
            '/following',
            '/video',
            '/story',
            '/chat',
            '/settings',
            '/user/:userId',
            '/user/:userId/:listType',
            '/user/:userId/:listType/:publishStatus',
            '/u/:username',
            '/u/:username/:listType',
            '/u/:username/:listType/:publishStatus',
            '/join/:progress',
            '/age-verify/:progress',
          ]}
          element={<ClientContainer filename="SwagChatLink" />}
        />
      </ClientContainer>
      {/* FloatingActionButtons */}

      <Routes>
        {/* Use <NavHeader /> Routes */}
        <Route
          path="/"
          element={
            <>
              <UniversalContainer filename="NavHeader" />
              <Outlet />
            </>
          }
        >
          <Route
            path=""
            element={
              <>
                <UniversalContainer filename="NeedAuth">
                  <UniversalContainer
                    data-key="authed"
                    filename="OpenEmailBindingAlert"
                  />
                </UniversalContainer>

                <UniversalContainer filename="Home" />
              </>
            }
          />
          <Route
            path="video"
            element={<UniversalContainer filename="Home" />}
          />
          <Route path="story" element={<Redirect to="/video" />} />

          <Route path="feed" element={<UniversalContainer filename="Feed" />} />

          <Route path="shorts" element={<Outlet />}>
            <Route index element={<UniversalContainer filename="Shorts" />} />
            <Route
              path={`:messageId/${ProfileListPublishStatus.DRAFT}`}
              element={
                <RequireAuth>
                  <MongoObjectIdMatcher paramKeys={['messageId']}>
                    <UniversalContainer filename="ShortsDraft" />
                  </MongoObjectIdMatcher>
                </RequireAuth>
              }
            />
            <Route
              path=":messageId"
              element={<UniversalContainer filename="ShortsDetail" />}
            />
            <Route
              path="ad/:cta"
              element={<UniversalContainer filename="ShortsDetail" />}
            />
          </Route>

          <Route
            path="following"
            element={<UniversalContainer filename="Home" />}
          />
          <Route path="explore" element={<Redirect to="/" />} />

          <Route path="post/:messageId" element={<Outlet />}>
            <Route
              index
              element={
                <MongoObjectIdMatcher paramKeys={['messageId']}>
                  <UniversalContainer filename="MessageDetailLayoutSelector" />
                </MongoObjectIdMatcher>
              }
            />
            <Route
              path="clip/:range"
              element={
                <MongoObjectIdMatcher paramKeys={['messageId']}>
                  <UniversalContainer filename="PostClipDraft" />
                </MongoObjectIdMatcher>
              }
            />
          </Route>

          <Route
            path="user/:userId/:listType/:publishStatus"
            element={
              <MongoObjectIdMatcher paramKeys={['userId']}>
                <UniversalContainer filename="UserByUserId" />
              </MongoObjectIdMatcher>
            }
          />
          <Route
            path="user/:userId/:listType"
            element={
              <MongoObjectIdMatcher paramKeys={['userId']}>
                <UniversalContainer filename="UserByUserId" />
              </MongoObjectIdMatcher>
            }
          />
          <Route
            path="user/:userId"
            element={
              <MongoObjectIdMatcher paramKeys={['userId']}>
                <UniversalContainer filename="UserByUserId" />
              </MongoObjectIdMatcher>
            }
          />

          <Route
            path="u/:username/:listType/:publishStatus"
            element={<UniversalContainer filename="UserByUsername" />}
          />
          <Route
            path="u/:username/:listType"
            element={<UniversalContainer filename="UserByUsername" />}
          />
          <Route
            path="u/:username"
            element={<UniversalContainer filename="UserByUsername" />}
          />

          <Route
            path={`user/:userId/${LIVESTREAM_PATH}`}
            element={
              <MongoObjectIdMatcher paramKeys={['userId']}>
                <UniversalContainer
                  filename="LiveStreams"
                  footerNavigationHeight={
                    getIsIphoneXSeriesPWA()
                      ? footerNavigationHeight + iPhoneXSeriesFooterPaddingInPWA
                      : footerNavigationHeight
                  }
                />
              </MongoObjectIdMatcher>
            }
          />

          <Route
            path="/story/:messageId"
            element={
              <MongoObjectIdMatcher paramKeys={['messageId']}>
                <UniversalContainer filename="StoryMessage" />
              </MongoObjectIdMatcher>
            }
          />

          <Route
            path="/message/:messageId"
            element={
              <MongoObjectIdMatcher paramKeys={['messageId']}>
                <UniversalContainer filename="StoryMessage" shouldLoop={true} />
              </MongoObjectIdMatcher>
            }
          />

          <Route
            path="/category-overview"
            element={<UniversalContainer filename="MessageCategoryMore" />}
          />
          <Route
            path="/category/:categoryId"
            element={
              <UniversalContainer filename="MessageCategoryFeedsById">
                <Outlet />
              </UniversalContainer>
            }
          >
            <Route
              index
              element={
                <UniversalContainer filename="MessageCategoryTabAllInOne" />
              }
            />
            <Route
              path=":listType"
              element={
                <UniversalContainer filename="MessageCategoryTabContent" />
              }
            />
          </Route>

          <Route
            path="/chat/:chatroomId/message/:messageId"
            element={
              <RequireAuth>
                <MongoObjectIdMatcher paramKeys={['chatroomId', 'messageId']}>
                  <UniversalContainer
                    filename="PostDetail"
                    isChatroomMessageLayout
                  />
                </MongoObjectIdMatcher>
              </RequireAuth>
            }
          />

          <Route
            path="chat"
            element={
              <UniversalContainer filename="ChatResponsiveDesignLayout">
                <Outlet />
              </UniversalContainer>
            }
          >
            <Route
              index
              element={<UniversalContainer filename="ChatDefaultRenderer" />}
            />
            <Route
              path="start"
              element={<UniversalContainer filename="ChatStart" via="chat" />}
            />
            <Route
              path=":chatroomId/media"
              element={
                <RequireAuth>
                  <MongoObjectIdMatcher paramKeys={['chatroomId']}>
                    <UniversalContainer filename="ChatMediaLibrary" />
                  </MongoObjectIdMatcher>
                </RequireAuth>
              }
            />
            <Route
              path=":chatroomId/search"
              element={
                <RequireAuth>
                  <MongoObjectIdMatcher paramKeys={['chatroomId']}>
                    <UniversalContainer filename="ChatroomSearch" />
                  </MongoObjectIdMatcher>
                </RequireAuth>
              }
            />
            <Route
              path={`${AUTO_CHAT_CHAT_ID}`}
              element={
                <RequireAuth>
                  <UniversalContainer
                    filename="AutoChatFeed"
                    id={AUTO_CHAT_CHAT_ID}
                  />
                </RequireAuth>
              }
            />
            <Route
              path=":chatroomId"
              element={
                <RequireAuth>
                  <MongoObjectIdMatcher paramKeys={['chatroomId']}>
                    <UniversalContainer filename="Chat" />
                  </MongoObjectIdMatcher>
                </RequireAuth>
              }
            />
            <Route
              path="user/:userId"
              element={
                <MongoObjectIdMatcher paramKeys={['userId']}>
                  <UniversalContainer
                    filename="Chat"
                    chatroomId={FAKE_CHAT_ID}
                  />
                </MongoObjectIdMatcher>
              }
            />
          </Route>

          <Route
            path="search"
            element={
              <UniversalContainer filename="SearchLayoutComponent">
                <Outlet />
              </UniversalContainer>
            }
          >
            <Route
              index
              element={<Redirect to={`search/${searchType.ALL}`} />}
            />
            <Route
              path=":type/:category"
              element={<UniversalContainer filename="Search" />}
            />
            <Route
              path=":type"
              element={<UniversalContainer filename="Search" />}
            />
          </Route>

          <Route
            path="product/:productId"
            element={<UniversalContainer filename="ProductDetail" />}
          />
          <Route
            path="user-feed-grid/:category"
            element={<UniversalContainer filename="UserFeedGridList" />}
          />
          <Route
            path="user-leaderboard-grid/:category"
            element={
              <UniversalContainer filename="UserFeedGridList" isLeaderboard />
            }
          />
          <Route
            path="user-following-grid/:category"
            element={<UniversalContainer filename="FollowingUserGridList" />}
          />
          <Route
            path="user-event"
            element={<UniversalContainer filename="UserEventInfo" />}
          />
          <Route
            path="settings/vvip"
            element={
              <RequireAuth>
                <UniversalContainer filename="UserLevel" />
              </RequireAuth>
            }
          />
          <Route
            path="/iframe"
            element={<UniversalContainer filename="IframeViewer" />}
          />
        </Route>
        {/* Use <NavHeader /> Routes */}

        <Route path="livestream" element={<Outlet />}>
          <Route index element={<UniversalContainer filename="Home" />} />
          <Route
            path=":index"
            element={<UniversalContainer filename="LivestreamIndex" />}
          />
        </Route>

        <Route
          path={`/user/:userId/${LIVESTREAM_PATH}/chat`}
          element={<UniversalContainer filename="LiveStreamPopOutChatroom" />}
        />

        <Route
          path="/story-categories/:category"
          element={<DiscoverShowMoreWrapper />}
        />

        <Route
          path="/shorts-categories/:category"
          element={<DiscoverShowMoreWrapper isShorts />}
        />

        <Route
          path="/story-preview/:messageId"
          element={
            <MongoObjectIdMatcher paramKeys={['messageId']}>
              <UniversalContainer filename="StoryPreview" />
            </MongoObjectIdMatcher>
          }
        />

        <Route
          path="/flix-categories/:category"
          element={<UniversalContainer filename="DiscoverFlixMore" />}
        />

        <Route
          path="/user-categories/:category"
          element={<UniversalContainer filename="UserMore" />}
        />

        <Route
          path="/leaderboard/:region/:category/:range"
          element={<UniversalContainer filename="LeaderboardCategory" />}
        />

        <Route
          path="/flix/:messageId"
          element={
            <MongoObjectIdMatcher paramKeys={['messageId']}>
              <Redirect to="/post/:messageId" status={301} />
            </MongoObjectIdMatcher>
          }
        />

        <Route
          path="/messages/:messageId"
          element={
            <MongoObjectIdMatcher paramKeys={['messageId']}>
              <Redirect to="/message/:messageId" />
            </MongoObjectIdMatcher>
          }
        />

        <Route
          path="/audio/:messageId"
          element={
            <RequireAuth>
              <MongoObjectIdMatcher paramKeys={['messageId']}>
                <UniversalContainer filename="AudioPlayerView" />
              </MongoObjectIdMatcher>
            </RequireAuth>
          }
        />
        <Route
          path="/user/:userId/chat"
          element={
            <MongoObjectIdMatcher paramKeys={['userId']}>
              <UniversalContainer filename="ChatByUserId" />
            </MongoObjectIdMatcher>
          }
        />

        <Route
          path="/hashtag/:hashtag"
          element={<Redirect to="/hashtag/:hashtag/flix" />}
        />
        <Route
          path="/hashtag/:hashtag/:type"
          element={<UniversalContainer filename="Hashtag" />}
        />

        <Route
          path={`/me/${ProfileListType.LIVESTREAM_SESSION}/:sessionId`}
          element={<UniversalContainer filename="MeLivestreamSession" />}
        />
        <Route
          path={`/me/${ProfileListType.LIVESTREAM_SESSION}/:sessionId/leaderboard`}
          element={
            <ModalTracking
              id="MeLivestreamSessionLeaderboard"
              modalId={ModalId.Profile.MyProfileLivestreamSessionLeaderboard}
            >
              <UniversalContainer filename="MeLivestreamSessionLeaderboard" />
            </ModalTracking>
          }
        />

        <Route path="/revenue" element={<Outlet />}>
          <Route
            index
            element={
              <RequireAuth>
                <UniversalContainer filename="RevenueSummary" />
              </RequireAuth>
            }
          />
          <Route
            path="report/daily"
            element={
              <RequireAuth>
                <UniversalContainer filename="RevenueDailyReport" />
              </RequireAuth>
            }
          />
          <Route
            path="report/fanleaderboard"
            element={
              <RequireAuth>
                <UniversalContainer filename="RevenueFanLeaderboard" />
              </RequireAuth>
            }
          />
          <Route
            path="report/billing"
            element={
              <RequireAuth>
                <UniversalContainer filename="RevenueBillingReport" />
              </RequireAuth>
            }
          />
        </Route>

        <Route
          path="/archive"
          element={
            <RequireAuth>
              <UniversalContainer filename="Archive" />
            </RequireAuth>
          }
        />

        <Route
          path="/archive/userId/:id/:listId"
          element={
            <RequireAuth>
              <UniversalContainer
                filename="PurchasedOutbox"
                footerNavigationHeight={
                  getIsIphoneXSeriesPWA()
                    ? footerNavigationHeight + iPhoneXSeriesFooterPaddingInPWA
                    : footerNavigationHeight
                }
              />
            </RequireAuth>
          }
        />

        <Route
          path="/messagePack/list"
          element={
            <RequireAuth>
              <UniversalContainer filename="MessagePackMore" />
            </RequireAuth>
          }
        />

        <Route
          path="/profile/following"
          element={
            <RequireAuth>
              <UniversalContainer
                filename="ProfileFollowing"
                via="personal_following"
              />
            </RequireAuth>
          }
        />
        <Route
          path="/profile/follower"
          element={
            <RequireAuth>
              <UniversalContainer
                filename="ProfileFollower"
                via="personal_follower"
              />
            </RequireAuth>
          }
        />

        <Route
          path="/profile/subscriber"
          element={
            <RequireAuth>
              <UniversalContainer filename="SubscriberList" />
            </RequireAuth>
          }
        />
        <Route
          path="/profile/subscribed"
          element={
            <RequireAuth>
              <UniversalContainer filename="SubscriptionList" />
            </RequireAuth>
          }
        />
        <Route
          path="/manage-profile"
          element={
            <RequireAuth>
              <Outlet />
            </RequireAuth>
          }
        >
          <Route
            path={`${ManageProfileType.MANAGE}`}
            element={
              <UniversalContainer filename="ManageProfile">
                <UniversalContainer filename="ManageProfileManage" />
              </UniversalContainer>
            }
          />
          <Route
            path={`${ManageProfileType.ACCOUNT}`}
            element={
              <UniversalContainer filename="ManageProfile">
                <UniversalContainer filename="ManageProfileAccount" />
              </UniversalContainer>
            }
          />

          <Route
            path="biography"
            element={<UniversalContainer filename="SetBiography" />}
          />
          <Route
            path="display-name"
            element={<UniversalContainer filename="SetDisplayName" />}
          />
          <Route path="account-linking" element={<Outlet />}>
            <Route
              index
              element={<UniversalContainer filename="AccountLinks" />}
            />
            <Route
              path="web-authn"
              element={<UniversalContainer filename="WebAuthnManagement" />}
            />
          </Route>
          <Route
            path="blocked-list"
            element={<UniversalContainer filename="ProfileBlocklist" />}
          />
          <Route path="credit-card" element={<Outlet />}>
            <Route
              index
              element={<UniversalContainer filename="PaymentMethodList" />}
            />
            <Route
              path="billing-email"
              element={<UniversalContainer filename="PaymentEmailSetting" />}
            />
          </Route>
          <Route path="receipt" element={<Outlet />}>
            <Route
              index
              element={<UniversalContainer filename="ReceiptSetting" />}
            />
            <Route
              path="information"
              element={
                <UniversalContainer filename="BillingReceiptRecipient" />
              }
            />
          </Route>
        </Route>

        <Route
          path="/addvalue"
          element={
            <RequireAuth>
              <UniversalContainer filename="Addvalue" />
            </RequireAuth>
          }
        />

        <Route
          path="/login"
          element={
            <RequireNoneAuth>
              <UniversalComponent filename="LoginPageLayoutSelector" />
            </RequireNoneAuth>
          }
        />

        <Route
          path="/referral"
          element={<Redirect status={301} to="/settings/referral" />}
        />

        <Route path="/settings" element={<Outlet />}>
          <Route index element={<UniversalContainer filename="Settings" />} />
          <Route
            path="auto-addvalue"
            element={
              <RequireAuth>
                <UniversalContainer filename="AutoAddvalue" />
              </RequireAuth>
            }
          />
          <Route
            path="2257"
            element={<UniversalContainer filename="TwoTwoFiveSeven" />}
          />
          <Route
            path="journal"
            element={
              <RequireAuth>
                <UniversalContainer filename="Journal" />
              </RequireAuth>
            }
          />
          <Route
            path="redeem"
            element={<UniversalContainer filename="Redeem" />}
          />
          <Route
            path="bank-account"
            element={<UniversalContainer filename="BankAccount" />}
          />
          <Route
            path="notification"
            element={<UniversalContainer filename="ProfileNotification" />}
          />
          <Route
            path="preferences"
            element={<UniversalContainer filename="ProfilePreferences" />}
          />
          <Route path="faq" element={<UniversalContainer filename="Faq" />} />
          <Route
            path="referral"
            element={
              <RequireAuth>
                <UniversalContainer filename="Referral" />
              </RequireAuth>
            }
          />
          <Route
            path="referral/result"
            element={
              <RequireAuth>
                <UniversalContainer filename="Referral" />
              </RequireAuth>
            }
          />
          <Route path="more" element={<Outlet />}>
            <Route
              index
              element={<UniversalContainer filename="MoreSetting" />}
            />
            <Route
              path="debug"
              element={<UniversalContainer filename="DebugPanel" />}
            />
            <Route
              path="drm"
              element={<UniversalContainer filename="DrmPanel" />}
            />
            <Route
              path="about"
              element={<UniversalContainer filename="ProfileAboutSwag" />}
            />
            <Route
              path="delete-account"
              element={<UniversalContainer filename="DeleteAccount" />}
            />
          </Route>
        </Route>

        <Route
          path="/tos"
          element={<UniversalContainer filename="TermsOfService" />}
        />

        <Route
          path="/privacy"
          element={<UniversalContainer filename="Privacy" />}
        />

        <Route
          path="/join"
          element={
            <Redirect to={`/join/${JoinProgress.PERSONAL_INFORMATION}`} />
          }
        />
        <Route
          path="/join/:progress"
          element={<UniversalContainer filename="Join" />}
        />

        <Route
          path="/age-verify/:progress"
          element={<UniversalContainer filename="UserAgeVerify" />}
        />

        <Route path="/standalone" element={<Outlet />}>
          <Route path="contractor" element={<Outlet />}>
            <Route
              path="example"
              element={<UniversalComponent filename="ContractorExample" />}
            />
          </Route>
          <Route path="game" element={<Outlet />}>
            <Route
              path="sic-bo"
              element={<UniversalComponent filename="MockGameSicBo" />}
            />
          </Route>
          <Route path="player" element={<Outlet />}>
            <Route
              path="byteplus-rtc"
              element={
                <UniversalContainer filename="StandalonePlayerByteplusRtc" />
              }
            />
          </Route>
          <Route path="player" element={<Outlet />}>
            <Route
              path="byteplus/:streamId"
              element={
                <UniversalContainer filename="StandalonePlayerByteplus" rtm />
              }
            />
            <Route
              path="byteplus"
              element={
                <UniversalContainer filename="StandalonePlayerByteplus" rtm />
              }
            />
          </Route>
        </Route>

        <Route
          path="/404"
          element={<UniversalContainer filename="NotFoundPage" />}
        />
        <Route path="/error" element={<ErrorPage />} />
        <Route
          path="*"
          element={<UniversalContainer filename="NotFoundPage" />}
        />
      </Routes>
      <MultiPathRoute
        paths={FOOTER_PATHS}
        element={<UniversalContainer filename="Footer" />}
      />

      <UniversalContainer filename="NeedAuth">
        <UniversalContainer
          data-key="authed"
          filename="OpenNotificationAsker"
        />
      </UniversalContainer>
      <RegExpRoute
        regExp={/^\/(?!join|revenue)/i}
        element={
          <ModalView
            id="GeneralUrlPopup"
            noScrollLock
            render={() => <UniversalContainer filename="GeneralUrlPopup" />}
          />
        }
      />
      <RegExpRoute
        regExp={/^\/(?!join|revenue)/i}
        element={
          <ModalView
            id="NotificationAsker"
            noScrollLock
            render={() => <UniversalContainer filename="NotificationAsker" />}
          />
        }
      />
      <RegExpRoute
        regExp={/^\/(?!join|revenue)/i}
        element={
          <ModalView
            id="PwaInstaller"
            noScrollLock
            render={() => <UniversalContainer filename="PwaInstaller" />}
          />
        }
      />
      <RegExpRoute
        regExp={/^\/(?!age-verify)/i}
        element={
          <ModalView
            id={registerModal.REGISTER_EMAIL}
            modalId={ModalId.Bind.BindEmailEnter}
            render={() => <UniversalContainer filename="RegisterEmail" />}
          />
        }
      />
      <RegExpRoute
        regExp={/^\/(?!age-verify)/i}
        element={
          <ModalView
            id={loginModal.AGE_VERIFICATION_OPTIONS}
            modalId={ModalId.LoginSignup.AgeVerificationPopup}
            render={() => (
              <UniversalContainer filename="AgeVerificationOptions" />
            )}
          />
        }
      />
      <ModalView
        id="Toastr"
        noScrollLock
        render={() => <UniversalContainer filename="Toastr" />}
      />
      <ModalView
        id="InAppBrowserAlert"
        noScrollLock
        render={() => <UniversalContainer filename="InAppBrowserAlert" />}
      />
      <ModalView
        id="MobileAppDownloader"
        noScrollLock
        render={() => (
          <UniversalComponent filename="DropDownPopup">
            <UniversalContainer filename="MobileAppDownloader" />
          </UniversalComponent>
        )}
      />
      <ModalView
        id="MobileAppTutorial"
        render={() => <UniversalContainer filename="MobileAppTutorial" />}
      />
      <UniversalContainer filename="RegisterAndLoginModals" />
      {shouldShowPwaSafariToast && (
        <ModalView
          id="PwaSafariToast"
          modalId={ModalId.Popup.AddToHomeScreen}
          noScrollLock
          render={() => <UniversalContainer filename="PwaSafariToast" />}
        />
      )}
      <ModalView
        id="DailyLoginRewardToastr"
        noScrollLock
        render={() => <UniversalContainer filename="DailyLoginRewardToastr" />}
      />
      <ModalView
        id="DailyLoginRewardDetail"
        noScrollLock
        render={() => <UniversalContainer filename="DailyLoginRewardDetail" />}
      />
      <ModalView
        id="PriceRefreshModal"
        render={() => <UniversalContainer filename="PriceRefreshModal" />}
      />
      <ModalView
        id="PrepaidCodeConfirmModal"
        modalId={ModalId.Prepaid.PrepaidConfirmPopup}
        render={() => <UniversalContainer filename="PrepaidCodeConfirmModal" />}
      />
      <ModalView
        id="PrepaidCodeSuccessModal"
        modalId={ModalId.Prepaid.PrepaidSuccessPopup}
        render={() => <UniversalContainer filename="PrepaidCodeSuccessModal" />}
      />
      <ModalView
        modalId={ModalId.Chatroom.CpEventPage}
        id="ChatEventModal"
        render={() => <UniversalContainer filename="ChatEventModal" />}
      />
      <ModalView
        id="SetPasswordDirectorModal"
        modalId={ModalId.LoginSignup.NotSetPasswordPopup}
        render={() => (
          <UniversalContainer filename="SetPasswordDirectorModal" />
        )}
      />
      <ModalView
        id="YaboAddvalueConfirmModal"
        render={() => (
          <UniversalContainer filename="YaboAddvalueConfirmModal" />
        )}
      />
      <ModalView
        id="BlockUserModal"
        render={() => <UniversalContainer filename="BlockUserModal" />}
      />
      <ModalView
        modalId={ModalId.Popup.DrmCannotPlayPopup}
        id="DrmNotSupportAlert"
        render={() => <UniversalContainer filename="DrmNotSupportAlert" />}
      />
      <ModalView
        modalId={ModalId.HamburgerMenu.Menu}
        id="HamburgerMenuModal"
        render={() => (
          <UniversalContainer
            filename="HamburgerMenuModal"
            paddingFooterNavigationHeight={
              getIsIphoneXSeriesPWA()
                ? footerNavigationHeight + iPhoneXSeriesFooterPaddingInPWA
                : footerNavigationHeight
            }
          />
        )}
      />
      <ModalView
        id="CopyConfirmModal"
        render={() => <UniversalContainer filename="CopyConfirmModal" />}
      />
      <ModalView
        id="VerifyFreeUserModal"
        modalId={ModalId.Popup.CardVerify}
        render={() => <UniversalContainer filename="VerifyFreeUserModal" />}
      />
      <ModalView
        id="TappayAddNewCardModal"
        render={() => <UniversalContainer filename="TappayAddNewCardModal" />}
      />
      <ModalView
        id="SecurionpayAddNewCardModal"
        render={() => (
          <UniversalContainer filename="SecurionpayAddNewCardModal" />
        )}
      />
      <UniversalContainer filename="LanguageMenuModalView" />
      <ModalView
        modalId={ModalId.Join.ModalBecomeCreator}
        id="BecomeCreatorModal"
        render={() => <UniversalContainer filename="BecomeCreatorModal" />}
      />
      <ModalView
        modalId={ModalId.Join.ModalDownloadApp}
        id="DownloadCreatorModal"
        render={() => <UniversalContainer filename="DownloadCreatorModal" />}
      />
      <ModalView
        id="ImageCover"
        render={() => <UniversalContainer filename="ImageCover" />}
      />
      <ModalView
        id="BackpackModal"
        modalId={ModalId.Backpack.ModalBackpack}
        render={() => <UniversalContainer filename="BackpackModal" />}
      />
      <ModalView
        id="PostSummaryModal"
        modalId={ModalId.Post.ModalPostSummaryDetail}
        render={() => <UniversalContainer filename="PostSummaryModal" />}
      />
      <ModalView
        id="PostLikesModal"
        modalId={ModalId.Post.ModalPostLikeDetail}
        render={() => <UniversalContainer filename="PostLikesModal" />}
      />
      <ModalView
        id="PostAssetFullscreenModal"
        render={() => (
          <UniversalContainer filename="PostAssetFullscreenModal" />
        )}
      />
      <UniversalContainer filename="SubscriptionModals" />
      <UniversalComponent filename="MessagePackModals" />
      <ModalView
        modalId={ModalId.Popup.ModalMessageMore}
        id="MessageMoreModal"
        render={() => <UniversalContainer filename="MessageMoreModal" />}
      />
      <ModalView
        id="UploadMenuModal"
        modalId={ModalId.Upload.ModalCreate}
        render={() => <UniversalContainer filename="UploadMenuModal" />}
      />
      <ModalView
        id="UploadJobMoreModal"
        modalId={ModalId.Upload.ModalUploadMore}
        render={() => <UniversalContainer filename="UploadJobMoreModal" />}
      />
      <ModalView
        id="PostDraft"
        render={() => <UniversalContainer filename="PostDraft" />}
      />
      <ModalView
        modalId={ModalId.Notification.NotificationCenterList}
        id="NotificationCenterModal"
      >
        <UniversalContainer filename="NotificationCenterModal" />
      </ModalView>
      <ModalView
        id={SEARCH_MODAL_ID}
        render={() => <UniversalContainer filename="SearchModal" />}
      />
      <ModalView
        id="PinConfirmModal"
        render={() => <UniversalContainer filename="PinConfirmModal" />}
      />
      <ModalView
        id="ClipConfirmModal"
        render={() => (
          <UniversalContainer
            filename="ClipConfirmModal"
            modalId="ClipConfirmModal"
          />
        )}
      />
      <ModalView
        id="MessagePackUpsellingModal"
        render={() => (
          <UniversalContainer
            filename="MessagePackUpsellingModal"
            modalId="MessagePackUpsellingModal"
          />
        )}
      />
      <ModalView
        id="SettingWithoutSaveModal"
        render={() => <UniversalContainer filename="SettingWithoutSaveModal" />}
      />
      <ModalView id="HideMessageConfirmModal">
        <UniversalContainer
          filename="HideMessageConfirmModal"
          modalId="HideMessageConfirmModal"
        />
      </ModalView>
      <ModalView
        id="PublishPostConfirmModal"
        modalId={ModalId.Post.ModalPublishAlert}
      >
        <UniversalContainer
          filename="PublishPostConfirmModal"
          modalId="PublishPostConfirmModal"
        />
      </ModalView>
      <ModalView
        id="NotYetVerifiedAlert"
        modalId={ModalId.Bind.ModalNotYetVerified}
        noScrollLock
        render={() => <UniversalContainer filename="NotYetVerifiedAlert" />}
      />
      <ModalView modalId={ModalId.All.RatingModal} id="LivestreamRatingModal">
        <UniversalContainer
          filename="LivestreamRatingModal"
          trackingModalId={ModalId.All.RatingModal}
        />
      </ModalView>
      <ModalView
        id="IframeModal"
        render={() => <UniversalContainer filename="IframeModal" />}
      />
      <ModalView
        id="PhoneVerifyRewardModal"
        render={() => (
          <UniversalContainer
            filename="PhoneVerifyRewardModal"
            modalId="PhoneVerifyRewardModal"
          />
        )}
      />
    </StyledApp>
  );
};

App.propTypes = {
  request: PropTypes.object,
  t: PropTypes.func,
  i18n: PropTypes.object,
};

const StyledApp = styled.div``;

const StyledBody = ({ sheet, ...props }) => {
  if (sheet) {
    return (
      <StyleSheetManager sheet={sheet.instance}>
        <App {...props} />
      </StyleSheetManager>
    );
  } else {
    return <App {...props} />;
  }
};

StyledBody.propTypes = {
  ...App.propTypes,
  sheet: PropTypes.object,
};

export default withTranslation()(StyledBody);
